import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { User, Mail, Calendar, Shield, Clock, CheckCircle, XCircle } from 'lucide-react';

const UserManagement = ({ onBack }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await apiService.getAdminUsers();
      setUsers(data);
    } catch (error) {
      console.error("Failed to fetch users", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserClick = async (userId) => {
    setLoading(true);
    try {
      const data = await apiService.getAdminUserDetail(userId);
      setSelectedUser(data);
    } catch (error) {
      console.error("Failed to fetch user details", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-md overflow-hidden min-h-[500px]">
      <div className="p-4 border-b flex items-center justify-between bg-gray-50">
        <div className="flex items-center">
          <button 
            onClick={onBack} 
            className="mr-4 text-gray-600 hover:text-indigo-600 flex items-center transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            仪表盘
          </button>
          <h2 className="text-xl font-semibold text-gray-800">用户管理</h2>
        </div>
      </div>

      <div className="p-4">
        {loading && <div className="text-center py-10 text-gray-500">加载中...</div>}

        {!loading && !selectedUser && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">昵称</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">邮箱</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">管理员</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">注册时间</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map(u => (
                  <tr key={u.id} onClick={() => handleUserClick(u.id)} className="cursor-pointer hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{u.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{u.nickname}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{u.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {u.is_admin ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">是</span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">否</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{u.created_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && selectedUser && (
          <div className="animate-fade-in">
            <button 
              onClick={() => setSelectedUser(null)} 
              className="mb-6 text-indigo-600 hover:text-indigo-800 flex items-center font-medium transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              用户列表
            </button>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column: User Profile Card */}
              <div className="lg:col-span-1">
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 h-24"></div>
                  <div className="px-6 pb-6">
                    <div className="relative -mt-12 mb-4 flex justify-center">
                      <div className="h-24 w-24 rounded-full bg-white p-1 shadow-md">
                        <div className="h-full w-full rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-3xl font-bold">
                          {selectedUser.user.nickname.charAt(0).toUpperCase()}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold text-gray-900">{selectedUser.user.nickname}</h3>
                      <p className="text-sm text-gray-500 flex items-center justify-center mt-1">
                        <Mail size={14} className="mr-1" /> {selectedUser.user.email}
                      </p>
                    </div>

                    <div className="space-y-3 border-t pt-4">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500 flex items-center"><Shield size={16} className="mr-2" /> 用户ID</span>
                        <span className="font-medium text-gray-900">#{selectedUser.user.id}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500 flex items-center"><Calendar size={16} className="mr-2" /> 注册时间</span>
                        <span className="font-medium text-gray-900">{selectedUser.user.created_at?.split('T')[0]}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500 flex items-center"><User size={16} className="mr-2" /> 角色</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${selectedUser.user.is_admin ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                          {selectedUser.user.is_admin ? '管理员' : '普通用户'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Subscriptions */}
              <div className="lg:col-span-2">
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden h-full">
                  <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                    <h3 className="font-bold text-gray-800 flex items-center">
                      <Clock size={18} className="mr-2 text-indigo-600" /> 
                      订阅服务记录
                    </h3>
                    <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded border">
                      共 {selectedUser.subscriptions.length} 条记录
                    </span>
                  </div>
                  
                  <div className="p-0">
                    {selectedUser.subscriptions.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-100">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">服务名称</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">有效期</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {selectedUser.subscriptions.map((s, index) => (
                              <tr key={s.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">{s.service_name}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-500 flex flex-col">
                                    <span>始: {s.start_time?.split('T')[0]}</span>
                                    <span>终: {s.end_time ? s.end_time.split('T')[0] : '永久有效'}</span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {s.is_valid ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      <CheckCircle size={12} className="mr-1" /> 生效中
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                      <XCircle size={12} className="mr-1" /> 已过期
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        <div className="inline-block p-3 rounded-full bg-gray-100 mb-3">
                          <Clock size={24} className="text-gray-400" />
                        </div>
                        <p>暂无订阅记录</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;